// components/ExplainabilityPanel.js
import React, { useState, useEffect } from 'react';

const ExplainabilityPanel = ({ explainability, isLoading }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [animatedSteps, setAnimatedSteps] = useState([]);

    // Animate steps appearing one by one
    useEffect(() => {
        if (explainability?.steps) {
            setAnimatedSteps([]);
            explainability.steps.forEach((step, index) => {
                setTimeout(() => {
                    setAnimatedSteps(prev => [...prev, step]);
                }, index * 150);
            });
        }
    }, [explainability]);

    if (!explainability && !isLoading) return null;

    const getSearchTypeColor = (searchType) => {
        const colorMap = {
            'lexical': '#10B981',      // Green
            'semantic': '#8B5CF6',     // Purple
            'similarity': '#F59E0B',   // Amber
            'feature_based': '#3B82F6', // Blue
            'reranked': '#EC4899',     // Pink
            'centroid': '#06B6D4',     // Cyan
            'error': '#EF4444',        // Red
        };
        return colorMap[searchType] || '#6B7280';
    };

    const getStepTypeColor = (stepType) => {
        const colorMap = {
            'lexical': { bg: 'rgba(16, 185, 129, 0.1)', border: '#10B981', text: '#059669' },
            'semantic': { bg: 'rgba(139, 92, 246, 0.1)', border: '#8B5CF6', text: '#7C3AED' },
            'classifier': { bg: 'rgba(59, 130, 246, 0.1)', border: '#3B82F6', text: '#2563EB' },
            'filter': { bg: 'rgba(245, 158, 11, 0.1)', border: '#F59E0B', text: '#D97706' },
            'reranker': { bg: 'rgba(236, 72, 153, 0.1)', border: '#EC4899', text: '#DB2777' },
            'centroid': { bg: 'rgba(6, 182, 212, 0.1)', border: '#06B6D4', text: '#0891B2' },
            'output': { bg: 'rgba(107, 114, 128, 0.1)', border: '#6B7280', text: '#4B5563' },
        };
        return colorMap[stepType] || colorMap['output'];
    };

    const formatLatency = (ms) => {
        if (ms < 1000) return `${Math.round(ms)}ms`;
        return `${(ms / 1000).toFixed(2)}s`;
    };

    const getLatencyColor = (ms) => {
        if (ms < 500) return '#10B981';   // Green - Fast
        if (ms < 1500) return '#F59E0B';  // Amber - Medium
        return '#EF4444';                  // Red - Slow
    };

    const renderFeaturePills = (features) => {
        if (!features || features.length === 0) return null;

        return (
            <div className="feature-pills-container">
                {features.map((feature, idx) => {
                    const key = Object.keys(feature)[0];
                    const value = feature[key];
                    return (
                        <span key={idx} className="feature-pill">
                            <span className="feature-key">{key}:</span>
                            <span className="feature-value">{String(value)}</span>
                        </span>
                    );
                })}
            </div>
        );
    };

    return (
        <div className={`explainability-panel ${isExpanded ? 'expanded' : 'collapsed'}`}>
            {/* Header */}
            <div
                className="explainability-header"
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <div className="header-left">
                    <span className="brain-icon">🔬</span>
                    <h3>Search Explainability</h3>
                </div>
                <div className="header-right">
                    {explainability && (
                        <span
                            className="latency-badge"
                            style={{
                                backgroundColor: `${getLatencyColor(explainability.total_latency_ms)}20`,
                                color: getLatencyColor(explainability.total_latency_ms)
                            }}
                        >
                            ⚡ {formatLatency(explainability.total_latency_ms)}
                        </span>
                    )}
                    <button className="toggle-btn">
                        {isExpanded ? '▼' : '▶'}
                    </button>
                </div>
            </div>

            {/* Content */}
            {isExpanded && (
                <div className="explainability-content">
                    {isLoading ? (
                        <div className="loading-state">
                            <div className="loading-spinner"></div>
                            <p>Analyzing search execution...</p>
                        </div>
                    ) : explainability ? (
                        <>
                            {/* Summary Section */}
                            <div className="summary-section">
                                <div className="summary-grid">
                                    {/* Search Type */}
                                    <div className="summary-card">
                                        <span className="summary-icon">🎯</span>
                                        <div className="summary-info">
                                            <span className="summary-label">Search Type</span>
                                            <span
                                                className="summary-value search-type-badge"
                                                style={{
                                                    backgroundColor: `${getSearchTypeColor(explainability.search_type)}20`,
                                                    color: getSearchTypeColor(explainability.search_type),
                                                    borderColor: getSearchTypeColor(explainability.search_type)
                                                }}
                                            >
                                                {explainability.search_type_label || explainability.search_type}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Results Count */}
                                    <div className="summary-card">
                                        <span className="summary-icon">📊</span>
                                        <div className="summary-info">
                                            <span className="summary-label">Results Found</span>
                                            <span className="summary-value">{explainability.result_count} movies</span>
                                        </div>
                                    </div>

                                    {/* Steps Count */}
                                    <div className="summary-card">
                                        <span className="summary-icon">🔄</span>
                                        <div className="summary-info">
                                            <span className="summary-label">Pipeline Steps</span>
                                            <span className="summary-value">{explainability.steps?.length || 0} executed</span>
                                        </div>
                                    </div>

                                    {/* Latency */}
                                    <div className="summary-card">
                                        <span className="summary-icon">⏱️</span>
                                        <div className="summary-info">
                                            <span className="summary-label">Total Latency</span>
                                            <span
                                                className="summary-value"
                                                style={{ color: getLatencyColor(explainability.total_latency_ms) }}
                                            >
                                                {formatLatency(explainability.total_latency_ms)}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Query Info */}
                                <div className="query-info">
                                    <div className="query-row">
                                        <span className="query-label">Original Query:</span>
                                        <code className="query-value">{explainability.query}</code>
                                    </div>
                                    {explainability.processed_query !== explainability.query && (
                                        <div className="query-row">
                                            <span className="query-label">Processed Query:</span>
                                            <code className="query-value processed">{explainability.processed_query}</code>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* LLM Relevance Explanation */}
                            {explainability.relevance_explanation && (
                                <div className="relevance-explanation-section">
                                    <h4>
                                        <span className="section-icon">💡</span>
                                        Why These Results?
                                    </h4>
                                    <div className="relevance-explanation-content">
                                        <p>{explainability.relevance_explanation}</p>
                                    </div>
                                </div>
                            )}

                            {/* Detected Features */}
                            {explainability.features_detected && explainability.features_detected.length > 0 && (
                                <div className="features-section">
                                    <h4>
                                        <span className="section-icon">🏷️</span>
                                        Detected Query Features
                                    </h4>
                                    {renderFeaturePills(explainability.features_detected)}
                                </div>
                            )}

                            {/* Pipeline Steps */}
                            <div className="pipeline-section">
                                <h4>
                                    <span className="section-icon">⚙️</span>
                                    Execution Pipeline
                                </h4>
                                <div className="pipeline-timeline">
                                    {animatedSteps.map((step, index) => {
                                        const colors = getStepTypeColor(step.step_type);
                                        return (
                                            <div
                                                key={index}
                                                className="pipeline-step"
                                                style={{
                                                    '--step-bg': colors.bg,
                                                    '--step-border': colors.border,
                                                    '--step-text': colors.text,
                                                    animationDelay: `${index * 0.1}s`
                                                }}
                                            >
                                                <div className="step-connector">
                                                    <div className="step-number">{step.step_number}</div>
                                                    {index < animatedSteps.length - 1 && (
                                                        <div className="connector-line"></div>
                                                    )}
                                                </div>
                                                <div className="step-content">
                                                    <div className="step-header">
                                                        <span className="step-icon">{step.icon}</span>
                                                        <span className="step-name">{step.name}</span>
                                                        <span className={`step-status ${step.status}`}>
                                                            {step.status === 'success' ? '✓' : '○'}
                                                        </span>
                                                    </div>
                                                    <div className="step-details">
                                                        <div className="step-details-badges">
                                                            {step.result_count > 0 && (
                                                                <span className="detail-badge results">
                                                                    {step.result_count} results
                                                                </span>
                                                            )}
                                                            {step.details && Object.entries(step.details).map(([key, value]) => {
                                                                // Skip centroid_explanation here, it will be shown separately
                                                                if (key === 'centroid_explanation') {
                                                                    return null;
                                                                }
                                                                // Skip sorting details, they'll be shown separately
                                                                if (key === 'smart_sorting' || key === 'sorting_method') {
                                                                    return null;
                                                                }
                                                                if (typeof value === 'boolean') {
                                                                    return value ? (
                                                                        <span key={key} className="detail-badge feature">
                                                                            {key.replace(/_/g, ' ')}
                                                                        </span>
                                                                    ) : null;
                                                                }
                                                                if (Array.isArray(value) && value.length > 0) {
                                                                    return (
                                                                        <span key={key} className="detail-badge array">
                                                                            {value.join(', ')}
                                                                        </span>
                                                                    );
                                                                }
                                                                if (typeof value === 'number' && value > 0) {
                                                                    return (
                                                                        <span key={key} className="detail-badge number">
                                                                            {key.replace(/_/g, ' ')}: {value}
                                                                        </span>
                                                                    );
                                                                }
                                                                return null;
                                                            })}
                                                        {step.details?.smart_sorting && (
                                                            <div className="sorting-explanation">
                                                                <span className="sorting-label">📊 Smart Sorting:</span>
                                                                <span className="sorting-text">{step.details.smart_sorting}</span>
                                                                {step.details.sorting_method && (
                                                                    <span className="sorting-method">{step.details.sorting_method}</span>
                                                                )}
                                                            </div>
                                                        )}
                                                        </div>
                                                        {step.details?.centroid_explanation && (
                                                            <div className="centroid-explanation">
                                                                <span className="centroid-label">🎯 Centroid Search:</span>
                                                                <span className="centroid-text">{step.details.centroid_explanation}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* System Info */}
                            <div className="system-info">
                                <div className="info-item">
                                    <span className="info-icon">🎨</span>
                                    <span>Centroid Search: {explainability.centroid_search_enabled ? 'Enabled' : 'Disabled'}</span>
                                </div>
                                <div className="info-item">
                                    <span className="info-icon">⚖️</span>
                                    <span>Reranker: {explainability.reranker_disabled ? 'Disabled' : 'Enabled'}</span>
                                </div>
                                {explainability.similarity_reference && (
                                    <div className="info-item">
                                        <span className="info-icon">🔗</span>
                                        <span>Similarity Ref: {explainability.similarity_reference}</span>
                                    </div>
                                )}
                            </div>
                        </>
                    ) : (
                        <div className="empty-state">
                            <span className="empty-icon">🔍</span>
                            <p>Search for movies to see execution details</p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ExplainabilityPanel;
